import React from 'react';
import { Button } from 'primereact/button';
import {
    SP_SKIP, extractResponseData, SmartStyles, SmartHeader, SmartSection, NoRecordsCard
} from './_SmartFieldRenderer';

// The live tan-advanced payload nests almost everything under `details`
// (details.full_name, details.address.full, …) rather than the flat keys the
// plain /tan/ endpoint returns. The old flat-only key list matched none of it,
// so the entire report rendered as one unlabelled blob under OTHER DETAILS.
//
// Each display key maps to candidate dot-paths, first non-empty wins. Kept in
// step with TanAdvancedPdfController::SECTIONS.
const GROUPS = {
    tan: {
        title: 'TAN DETAILS', icon: 'pi-id-card',
        fields: {
            tan_number:         ['tan_number', 'tan'],
            name:               ['details.full_name', 'name'],
            first_name:         ['details.first_name'],
            middle_name:        ['details.middle_name'],
            last_name:          ['details.last_name'],
            tan_allotment_date: ['details.tan_allotment_date'],
            status:             ['status'],
            category:           ['category'],
        },
    },
    contact: {
        title: 'CONTACT', icon: 'pi-phone',
        fields: {
            email:           ['details.email_1', 'email'],
            alternate_email: ['details.email_2'],
            phone:           ['details.phone_number', 'phone'],
            designation:     ['designation'],
        },
    },
    address: {
        title: 'ADDRESS', icon: 'pi-map-marker',
        fields: {
            address:    ['details.address.full'],
            city:       ['city'],
            state:      ['state'],
            state_code: ['details.address.state_code'],
            pin:        ['details.address.zip', 'pin'],
        },
    },
};

// Claimed so they don't resurface under OTHER DETAILS, but not shown:
// address.full is the verbatim concatenation of these lines.
const SUPPRESSED = [
    'details.address.line_1', 'details.address.line_2', 'details.address.line_3',
    'details.address.line_4', 'details.address.line_5',
];

const HIGHLIGHT = ['tan_number'];

const getPath = (obj, path) => path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);

// Flattens to dot-paths so an unmapped key nested under `details` renders as one
// labelled row instead of dumping the whole object.
const flatten = (obj, prefix = '', out = {}) => {
    for (const [k, v] of Object.entries(obj || {})) {
        const path = prefix ? `${prefix}.${k}` : k;
        if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, path, out);
        else out[path] = v;
    }
    return out;
};

// `onDownloadPDF`/`pdfPasswordHint` are optional. The shared-link and lead
// history views render this component without them, and those readers have no
// business regenerating a billed report's PDF — so the button only appears when
// the host actually wires it up.
const TanAdvancedOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details. Please verify and try again." />;
    }

    const d = responseData;
    const grouped = {};
    for (const [groupName, group] of Object.entries(GROUPS)) {
        const picked = {};
        for (const [displayKey, candidates] of Object.entries(group.fields)) {
            const hit = candidates.map(p => getPath(d, p)).find(v => v !== null && v !== undefined && v !== '');
            if (hit !== undefined) picked[displayKey] = hit;
        }
        if (Object.keys(picked).length > 0) grouped[groupName] = picked;
    }

    const claimed = new Set([
        ...SUPPRESSED,
        ...Object.values(GROUPS).flatMap(g => Object.values(g.fields).flat()),
    ]);
    const leftover = {};
    for (const [path, v] of Object.entries(flatten(d))) {
        const leaf = path.includes('.') ? path.slice(path.lastIndexOf('.') + 1) : path;
        if (claimed.has(path) || SP_SKIP.has(path) || SP_SKIP.has(leaf)) continue;
        if (v === null || v === undefined || v === '') continue;
        leftover[leaf] = v;
    }

    return (
        <div>
            <SmartStyles />
            <SmartHeader title="TAN Advanced Report" icon="pi-search-plus" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />
            <div className="horizontal-report-card">
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button
                                label="Download PDF"
                                icon="pi pi-file-pdf"
                                size="small"
                                outlined
                                onClick={onDownloadPDF}
                            />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}
                {Object.entries(GROUPS).map(([groupName, meta]) => (
                    grouped[groupName]
                        ? <SmartSection key={groupName} title={meta.title} icon={meta.icon} data={grouped[groupName]} highlightKeys={HIGHLIGHT} />
                        : null
                ))}
                {Object.keys(leftover).length > 0 && (
                    <SmartSection title="OTHER DETAILS" icon="pi-info-circle" data={leftover} />
                )}
            </div>
        </div>
    );
};

export default TanAdvancedOutputComponent;
